import React from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  selectCartItems,
  selectCartTotal,
} from '../../redux/selectors/cartSelector';
import {
  clearCart,
  decrementQuantity,
  incrementQuantity,
  removeItemFromCart,
} from '../../redux/slices/cartSlice';
import { setSelectedProduct } from '../../redux/slices/productSlice';
import { updatePrice } from '../../utils/helper';
import Breadcrumb from '../../components/Breadcrumb';
import Button from '../../components/Button';

const CartMain = () => {
  const products = useSelector(selectCartItems); // selector to get all products in cartItems
  const cartTotal = useSelector(selectCartTotal); // selector to get cart total amount
  const dispatch = useDispatch();

  // Dispatch the action the to get product details data
  const handleProductDetails = (product) => {
    dispatch(setSelectedProduct({ ...product, productId: product.id }));
  };

  // Dispatch the action to increase the quantity
  const handleIncrement = (id) => {
    dispatch(incrementQuantity(id));
  };

  // Dispatch the action to decrease the quantity
  const handleDecrement = (id) => {
    dispatch(decrementQuantity(id));
  };

  // Dispatch the action to remove product from cart
  const handleRemove = (id, title) => {
    dispatch(removeItemFromCart({ id: id, title: title }));
  };

  // Dispatch the action to remove all products from cart
  const handleClearCart = () => {
    const confirmed = window.confirm(
      'Are you sure you want to clear the cart?'
    );
    if (confirmed) {
      dispatch(clearCart());
      toast.success('Cart cleared successfully!', { position: 'top-center' });
    }
  };

  return (
    <main>
      <Breadcrumb title="Cart" />

      {products.length === 0 ? (
        <div className="it-error-content text-center pt-120 pb-100">
          <h5
            className="it-error-title wow animate__fadeInUp"
            data-wow-duration=".9s"
            data-wow-delay=".3s"
          >
            Your Cart is Empty
          </h5>
          <p
            className="mb-25 wow animate__fadeInUp"
            data-wow-duration=".9s"
            data-wow-delay=".5s"
          >
            But it doesn't have to stay that way. <br /> Browse our collection
            and find something you love!
          </p>
          <Link
            className="it-btn-theme wow animate__fadeInUp"
            data-wow-duration=".9s"
            data-wow-delay=".7s"
            to="/shop-sidebar"
          >
            <Button btnText="Back To Shop" />
          </Link>
        </div>
      ) : (
        <section className="cart-area pt-120 pb-120">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <form action="#">
                  <div
                    className="table-content table-responsive wow animate__fadeInUp"
                    data-wow-duration=".9s"
                    data-wow-delay=".3s"
                  >
                    <table className="table">
                      <thead>
                        <tr>
                          <th className="product-thumbnail">Images</th>
                          <th className="cart-product-name">Product</th>
                          <th className="product-price">Unit Price</th>
                          <th className="product-quantity">Quantity</th>
                          <th className="product-subtotal">Total</th>
                          <th className="product-remove">Remove</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products.map((product) => (
                          <tr key={product.id}>
                            <td className="product-thumbnail">
                              <Link
                                to={`/shop/${product.id}`}
                                onClick={() => handleProductDetails(product)}
                              >
                                <img src={product.thumbnail} alt="" />
                              </Link>
                            </td>
                            <td className="product-name">
                              <Link
                                to={`/shop/${product.id}`}
                                onClick={() => handleProductDetails(product)}
                              >
                                {product.title}
                              </Link>
                            </td>
                            <td className="product-price">
                              <span className="amount">
                                ${Math.round(updatePrice(product)).toFixed(2)}
                              </span>
                            </td>
                            <td className="product-quantity">
                              <span
                                className="cart-minus"
                                onClick={() => handleDecrement(product.id)}
                              >
                                -
                              </span>
                              <input
                                className="cart-input"
                                type="text"
                                value={product.quantity}
                                readOnly
                              />
                              <span
                                className="cart-plus"
                                onClick={() => handleIncrement(product.id)}
                              >
                                +
                              </span>
                            </td>
                            <td className="product-subtotal">
                              <span className="amount">
                                $
                                {(
                                  Math.round(updatePrice(product)) *
                                  product.quantity
                                ).toFixed(2)}
                              </span>
                            </td>
                            <td className="product-remove">
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemove(product.id, product.title)
                                }
                              >
                                <i className="fa fa-times"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="coupon-all">
                        <div className="coupon d-flex align-items-center flex-wrap">
                          <input
                            id="coupon_code"
                            className="input-text mb-20"
                            name="coupon_code"
                            placeholder="Coupon code"
                            type="text"
                          />
                          <button
                            className="it-btn-theme mb-20"
                            name="apply_coupon"
                            type="submit"
                          >
                            <Button btnText="Apply coupon" />
                          </button>
                        </div>
                        <div className="coupon2">
                          <button
                            className="it-btn-theme mb-20"
                            name="update_cart"
                            type="submit"
                            onClick={handleClearCart}
                          >
                            <Button btnText="Clear Cart" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row justify-content-end">
                    <div className="col-md-5 ">
                      <div className="cart-page-total">
                        <h2>Cart totals</h2>
                        <ul className="mb-20">
                          <li>
                            Subtotal
                            <span>${cartTotal.toFixed(2)}</span>
                          </li>
                          <li>
                            Total
                            <span>${cartTotal.toFixed(2)}</span>
                          </li>
                        </ul>
                        <Link className="it-btn-theme" to="/checkout">
                          <Button btnText="Proceed to checkout" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default CartMain;
